// Duplicated verbatim from Namat/mobile/src/lib/health/healthImportIds.ts, in
// the same way stepsTarget.ts already is. The two copies MUST agree: a
// difference in sanitizeRecordKey means web writes a tombstone under an id the
// phone never looks up, and a deleted entry silently comes back.
/**
 * Document ids for entries imported from a health store.
 *
 * The whole idempotency story rests on this file: an imported entry's id is
 * derived from what identifies that entry on the platform, so re-running an
 * import writes the SAME id and the pass can simply skip it. Same idea as
 * habitLogs' `${userId}_${habitId}_${YYYY-MM-DD}`, generalised.
 *
 * Nothing here imports Firebase, a health library, or a clock beyond the Date
 * it is handed.
 */

/**
 * `hsteps` is a whole day's step total, `hsleep` a whole night — for those two
 * the day IS the identity, because we deliberately write exactly one per
 * day/night. `hworkout` and `hmeal` need the platform's own record id, because
 * a day can hold several of each.
 */
export type HealthImportKind = 'hsleep' | 'hmeal' | 'hworkout' | 'hsteps';

export const HEALTH_IMPORT_KINDS: readonly HealthImportKind[] = [
  'hsleep',
  'hmeal',
  'hworkout',
  'hsteps',
];

const KIND_SET = new Set<string>(HEALTH_IMPORT_KINDS);

/** Local calendar day as YYYY-MM-DD. Mirrors formatDate in habits.ts and sleep.ts. */
export function dayKey(date: Date): string {
  const year = date.getFullYear();
  // String()/padStart, never toLocaleString: Western digits in both languages.
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * FNV-1a, 32-bit, base36. Not a security hash — its only job is to keep two
 * different raw keys apart after truncation, which plain truncation cannot do.
 * Hand-written because neither repo has a hash dependency and adding one for
 * seven characters is not worth a native build.
 */
export function shortHash(raw: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < raw.length; i++) {
    h ^= raw.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  // A 32-bit unsigned value is at most 7 base36 digits ('1z141z3').
  return h.toString(36).padStart(7, '0');
}

const MAX_READABLE = 64;

/**
 * A platform record id, made safe to use as a Firestore document id.
 *
 * The readable part is kept so an id is recognisable in the console, but the
 * hash suffix is what makes it CORRECT: an id longer than the cap, or one made
 * entirely of characters we strip (an Arabic food name, say), would otherwise
 * collapse into the same string as its neighbours and two different records
 * would fight over one document.
 *
 * An underscore is stripped like anything else, which is what lets
 * parseHealthImportDocId split reliably.
 */
export function sanitizeRecordKey(raw: string): string {
  const readable = raw
    .replace(/[^A-Za-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, MAX_READABLE);
  return readable ? `${readable}-${shortHash(raw)}` : shortHash(raw);
}

export function healthImportDocId(
  userId: string,
  kind: HealthImportKind,
  recordKey: string
): string {
  return `${userId}_${kind}_${sanitizeRecordKey(recordKey)}`;
}

/**
 * Split from the RIGHT. The sanitised key never contains an underscore and the
 * kind never does either, so the last two underscores are unambiguous — and a
 * userId that happens to contain one still parses. Splitting from the left
 * would break on exactly that case.
 *
 * Returns null for anything this module did not generate, which is how the
 * delete paths tell an imported entry from a normal addDoc id without a read.
 */
export function parseHealthImportDocId(
  docId: string
): { userId: string; kind: HealthImportKind; key: string } | null {
  const lastUnderscore = docId.lastIndexOf('_');
  if (lastUnderscore <= 0) return null;
  const prevUnderscore = docId.lastIndexOf('_', lastUnderscore - 1);
  if (prevUnderscore <= 0) return null;

  const userId = docId.slice(0, prevUnderscore);
  const kind = docId.slice(prevUnderscore + 1, lastUnderscore);
  const key = docId.slice(lastUnderscore + 1);
  if (!userId || !key || !KIND_SET.has(kind)) return null;

  return { userId, kind: kind as HealthImportKind, key };
}

export function isHealthImportDocId(docId: string): boolean {
  return parseHealthImportDocId(docId) !== null;
}
